import produce from 'immer';
import { WritableAtom, atom } from 'jotai';
import { Internal } from '@gleamer/types';
import { cloneDeep } from 'lodash';
import { RESET, atomWithStorage, createJSONStorage } from 'jotai/utils';
import { groupByStudyAndSeries } from '../utils/predictions.utils';

type PredictionInternal = Internal.PredictionInternal;
type StudyOrder = {
  studyInstanceUID: string;
  seriesInstanceUIDs: string[];
};
type SetPredictionsArgs = {
  predictions: PredictionInternal[];
  overrideInitialPredictions?: boolean;
};

export const displayPredictionsAtom = atom<boolean>(false);

export const toogleDisplayPredictionsAtom = atom(
  null,
  (get, set, newValue?: boolean) => {
    const update = newValue ?? !get(displayPredictionsAtom);
    set(displayPredictionsAtom, update);
  }
);

export const activeDetectionAtom = atom('');

export const studiesInstanceOrder = atom<StudyOrder[]>([]);

export const initalPredictionsAtom = atom<PredictionInternal[]>([]);

export const predictionsConfidencesAtom = atom([0, 100]);

export const onlyWithouStatusDetectionsAtom = atom(false);

export const predictionsStorage = createJSONStorage<PredictionInternal[]>(
  () => localStorage
);

export const currentPredictionKeyAtom = atom('');

/** The case's predictions could not be read: not the same as none, and no pass is validated on it. */
export const predictionsLoadFailedAtom = atom(false);

export const predictionsAtomsByIdAtom = atom(
  new Map<string, WritableAtom<PredictionInternal[], [any], void>>()
);

export const getOrCreateCurrentPredictionStorageAtom = atom((get) => {
  let predictionsKey = get(currentPredictionKeyAtom);
  let predictionsAtomsById = get(predictionsAtomsByIdAtom);

  if (predictionsKey === '') {
    if (predictionsAtomsById.has(predictionsKey)) {
      return predictionsAtomsById.get(predictionsKey);
    }
    let initialAtom = atom<PredictionInternal[]>([]);
    let newWritableAtom: WritableAtom<PredictionInternal[], [any], void> = atom(
      (_get) => _get(initialAtom),
      (_get, _set, predictions) => {
        _set(initialAtom, predictions);
      }
    );
    predictionsAtomsById.set(predictionsKey, newWritableAtom);
    return newWritableAtom;
  }

  if (!predictionsKey) {
    return null;
  }

  if (predictionsAtomsById.has(predictionsKey)) {
    return predictionsAtomsById.get(predictionsKey);
  }
  const initialValues = predictionsStorage.getItem(predictionsKey, []);
  let newAtom = atomWithStorage<PredictionInternal[]>(
    predictionsKey,
    initialValues,
    predictionsStorage
  );
  predictionsAtomsById.set(predictionsKey, newAtom);
  return newAtom;
});

export const setPredictionsAtom = atom(
  null,
  (get, set, newPredictionsArgs: SetPredictionsArgs) => {
    const { predictions: newPredictions, overrideInitialPredictions } =
      newPredictionsArgs;
    const existingPredictionsAtom = get(
      getOrCreateCurrentPredictionStorageAtom
    );
    const existingPredictions = get(existingPredictionsAtom);
    if (overrideInitialPredictions) {
      set(initalPredictionsAtom, cloneDeep(newPredictions));
    }

    let mergedPredictions = newPredictions;

    if (existingPredictions.length) {
      const existingDetections = existingPredictions.flatMap(
        (existingPrediction) => existingPrediction.detections
      );

      mergedPredictions = newPredictions.map((pred) => ({
        ...pred,
        detections: pred.detections.map((detection) => {
          const initialDetection = existingDetections.find(
            (existing) => existing.annotationId === detection.annotationId
          );
          return {
            ...detection,
            status: initialDetection?.status ?? detection.status,
          };
        }),
      }));
    }

    if (newPredictions[0]?.detections?.length) {
      set(activeDetectionAtom, newPredictions[0]?.detections[0].annotationId);
    }
    set(existingPredictionsAtom, mergedPredictions);
  }
);

const predictionsInConfidencesAtom = atom((get) => {
  let predictionsAtom = get(getOrCreateCurrentPredictionStorageAtom);
  if (!predictionsAtom) {
    return [];
  }
  const predictions = get(predictionsAtom) ?? [];
  const [minConfidence, maxConfidence] = get(predictionsConfidencesAtom);

  return (predictions || [])
    .map((prediction) => {
      return produce(prediction, (draft) => {
        draft.detections = draft.detections.filter((region) => {
          return (
            region.confidence >= minConfidence &&
            region.confidence <= maxConfidence
          );
        });
      });
    })
    .filter((prediction) => prediction.detections.length > 0);
});

/**
 * How far the review of the detections has gone: those in the confidence window, whether "Filter pending detections"
 * hides the decided ones or not.
 */
export const reviewProgressAtom = atom((get) => {
  const detections = get(predictionsInConfidencesAtom).flatMap(
    (prediction) => prediction.detections
  );
  return {
    reviewed: detections.filter((detection) => detection.status !== undefined)
      .length,
    total: detections.length,
  };
});

export const displayedPredictionsAtom = atom((get) => {
  const predictionsInConfidences = get(predictionsInConfidencesAtom);
  const onlyWithoutStatus = get(onlyWithouStatusDetectionsAtom);

  if (!onlyWithoutStatus) {
    return predictionsInConfidences;
  }

  return predictionsInConfidences
    .map((prediction) => {
      return produce(prediction, (draft) => {
        draft.detections = draft.detections.filter((region) => {
          return region.status === undefined;
        });
      });
    })
    .filter((prediction) => prediction.detections.length > 0);
});

export const displayedPredictionsBySeriesAtom = atom((get) => {
  const predictions = get(displayedPredictionsAtom);
  return groupByStudyAndSeries(predictions);
});

export const predictionsBySeriesAtom = atom((get) => {
  const predictions = get(predictionsInConfidencesAtom);
  return groupByStudyAndSeries(predictions);
});

export const incrementActiveDetectionIndexAtom = atom(null, (get, set) => {
  const predictionsByStudies = get(predictionsBySeriesAtom);
  const studyInstanceOrder = get(studiesInstanceOrder);
  const activeDetectionId = get(activeDetectionAtom);
  const onlyWithoutStatus = get(onlyWithouStatusDetectionsAtom);

  const detections = studyInstanceOrder.flatMap((study) =>
    study.seriesInstanceUIDs
      .flatMap((serie) => predictionsByStudies[study.studyInstanceUID]?.[serie])
      .flatMap((prediction) => prediction?.detections ?? [])
  );

  const detectionIndex = detections.findIndex(
    (detection) => detection.annotationId === activeDetectionId
  );

  const nextDetection = detections.find((detection, index) => {
    if (onlyWithoutStatus) {
      return detection.status === undefined && index > detectionIndex;
    }
    return index > detectionIndex;
  });

  if (nextDetection) {
    set(activeDetectionAtom, nextDetection.annotationId);
  }
});

export const setDetectionStatusAtom = atom(
  null,
  (get, set, { detectionId, status }) => {
    const predictionsAtom = get(getOrCreateCurrentPredictionStorageAtom);
    if (!predictionsAtom) {
      return;
    }

    const predictions = get(predictionsAtom);
    const predictionIndex = predictions.findIndex((prediction) =>
      prediction.detections.some(
        (detection) => detection.annotationId === detectionId
      )
    );

    if (predictionIndex === -1) {
      return;
    }

    const detectionIndex = predictions[predictionIndex].detections.findIndex(
      (detection) => detection.annotationId === detectionId
    );

    if (detectionIndex === -1) {
      return;
    }

    set(
      predictionsAtom,
      produce(predictions, (draft) => {
        draft[predictionIndex].detections[detectionIndex].status = status;
      })
    );
  }
);

export const resetPredictionsAtom = atom(null, (get, set) => {
  let currentKey = get(currentPredictionKeyAtom);
  let predictionsAtomsById = get(predictionsAtomsByIdAtom);
  set(initalPredictionsAtom, []);
  if (predictionsAtomsById.has('')) {
    set(predictionsAtomsById.get(''), []);
    predictionsAtomsById.delete('');
  }
  if (currentKey) {
    if (predictionsAtomsById.has(currentKey)) {
      let predictionsAtom = predictionsAtomsById.get(currentKey);
      set(predictionsAtom, RESET);
      predictionsAtomsById.delete(currentKey);
    }
    set(currentPredictionKeyAtom, '');
  }
  set(predictionsAtomsByIdAtom, new Map(predictionsAtomsById));
});
